import { Icon } from "@material-ui/core";
import moment from "moment";
import React, { Component } from "react";
import type Stripe from "stripe";
import styled, { css } from "styled-components";

import { DialogTitle, Link, MenuItem, Select } from "@material-ui/core";

import { IPromoDescription, IUserStorageModel, WorkspacePlanTier } from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";
import { upgradeWorkspacePlan } from "js/controllers/BillingController";
import { getWorkspacePlan } from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { BeautifulDialog, DialogContent, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap10, Gap30 } from "js/react/components/Gap";
import { Divider } from "js/react/components/UiComponents";

import { BodySmall } from "./FormStyling";
import PaymentForm from "./PaymentForm";
import SuccessfulPurchase from "./SuccessfulPurchase";
import { TermsOfUseAndRefundPolicy } from "./TermsOfUseAndRefundPolicy";

import "css/billing.scss";

const logger = getLogger(LogGroup.BILLING);

const LabelLarge = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.28px;
    margin: 0;
    color: #666;
`;

const RowFlex = css`
    display: flex;
    flex-direction: row;
`;

const Row = styled.div`
    ${RowFlex};
`;

const PriceComparisonContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const PriceComparisonRow = styled.div`
    ${RowFlex};
    justify-content: space-between;
`;

const AmountPaidContainer = styled.div`
    ${RowFlex};
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const AmountSavedContainer = styled.div`
    ${RowFlex};
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const StrikedAmount = styled.span`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    letter-spacing: -0.18px;
    text-decoration-line: line-through;
    color: #666;
`;

const Badge = styled.div`
    background: #96BB47;
    color: white;
    padding: 3px 6px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    text-transform: uppercase;
    border-radius: 1px;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    letter-spacing: -0.28px;
`;

const ChangePlanSection = styled.span`
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.28px;
    color: #666;

    @media (max-width: 768px) {
        flex: 1 1 auto;
    }
`;

const StyledLink = styled(Link)`
    &&& {
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 145%;
        letter-spacing: -0.28px;
        text-decoration-line: underline;
        color: #11a9e2;
    }
`;

const StyledCancelAnytimeText = styled.div`
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.28px;
    margin-top: 16px;
`;

const StyledCancelAnytimeTextBold = styled.span`
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 145%;
    letter-spacing: -0.28px;
`;

const StyledRow = styled(Row)`
    margin-top: 10px;

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
    }
`;

const ChoosePlanHeading = styled.div`
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    color: #222;
    margin-bottom: 18px;
`;

const PreloadContainer = styled.div`
    min-height: 500px;
`;

type CheckoutDialogContentProps = {
    hideBackdrop?: boolean;
    priceIds?: string[];
    defaultBillingInterval?: "month" | "year";
    productDisplayName?: string;
    closeDialog?: (upgraded?: boolean) => void;
}

interface CheckoutDialogState {
    billingInterval: "month" | "year";
    successfulPurchase: boolean;
    isSubmitting: boolean;
    isReady: boolean;
    availableReferralCredit: number;
    taxAmount: number | null;
    promo: IPromoDescription | null;
    prices: {
        month: RecurringPlanDefinition;
        year: RecurringPlanDefinition;
    };
    productDisplayName: string;
}

export class CheckoutDialogContent extends Component<CheckoutDialogContentProps, CheckoutDialogState> {
    constructor(props: CheckoutDialogContentProps) {
        super(props);

        const {
            defaultBillingInterval,
            priceIds,
            productDisplayName,
        } = props;

        const tier = WorkspacePlanTier.PRO;
        const productDefinition = stripeProducts[tier];
        const prices = {
            year: productDefinition.plans.find(plan => plan.isDefault && "interval" in plan && plan.interval === "year") as RecurringPlanDefinition,
            month: productDefinition.plans.find(plan => plan.isDefault && "interval" in plan && plan.interval === "month") as RecurringPlanDefinition
        };

        let billingInterval = defaultBillingInterval ?? "year";

        const requestedPriceDefinitions = priceIds?.map(id => productDefinition.plans.find(plan => "interval" in plan && plan.planId === id)).filter(Boolean) as RecurringPlanDefinition[] ?? [];
        requestedPriceDefinitions.forEach(price => {
            if (price.interval === "year") {
                prices.year = price;
            } else {
                prices.month = price;
            }
        });
        if (requestedPriceDefinitions.length === 1 && !defaultBillingInterval) {
            billingInterval = requestedPriceDefinitions[0].interval;
        }

        this.state = {
            billingInterval,
            successfulPurchase: false,
            isSubmitting: false,
            isReady: false,
            availableReferralCredit: 0,
            taxAmount: null,
            promo: null,
            prices,
            productDisplayName: productDisplayName ?? productDefinition.productName
        };
    }

    get user() {
        // This component can be used in absence of AppController, so we use app.user in that case
        return (app.user as IUserStorageModel);
    }

    async componentDidMount() {
        const firebaseUser = auth().currentUser;

        try {
            const { billingInterval, prices, productDisplayName } = this.state;

            const plan = await getWorkspacePlan("personal");
            if (plan.tier !== WorkspacePlanTier.BASIC) {
                ShowMessageDialog({
                    title: "Are you sure about this?",
                    message: `You are already paying for a ${plan.name} plan. Let's take you to your presentation library.`,
                    onClick: () => {
                        window.location.href = "/";
                    },
                    onClose: () => {
                        window.location.href = "/";
                    }
                });
                return;
            }

            const { totalAvailable } = await Api.referrals.get();
            this.setState({ availableReferralCredit: totalAvailable, isReady: true });

            // Legacy
            const { currentPlan, analyticsCurrentPlan } = await Api.trialDetail.get({ t: new Date().getTime() });
            app.user.analyticsPersonalPlan = analyticsCurrentPlan;

            const params = new URLSearchParams(window.location.search);
            const testId = params.get("ex");
            const props = {
                current_status: currentPlan,
                workspace_id: "personal",
                price_id: prices[billingInterval].planId,
                plan_display_name: productDisplayName,
                test_id: testId,
                user_id: firebaseUser.uid
            };
            trackActivity("Upgrade", "BillingIntentStart", testId, null, props, { audit: true });
            // End Legacy
        } catch (err) {
            logger.error(err, "[CheckoutDialogContent] componentDidMount() failed");
        }
    }

    handlePaymentFormSubmit = async ({
        paymentMethod,
        promo,
        billingAddress,
        taxId
    }: {
        paymentMethod: Stripe.PaymentMethod;
        promo: IPromoDescription;
        billingAddress: Stripe.Address;
        taxId?: Pick<Stripe.TaxId, "type" | "value">;
    }) => {
        const { billingInterval, prices } = this.state;

        this.setState({ isSubmitting: true });

        try {
            await upgradeWorkspacePlan({
                workspaceId: "personal",
                tier: WorkspacePlanTier.PRO,
                quantity: 1,
                priceId: prices[billingInterval].planId,
                paymentMethodId: paymentMethod.id,
                billingDetails: {
                    address: billingAddress,
                    taxId
                },
                promotionCode: promo?.code
            });

            this.setState({ successfulPurchase: true });
        } catch (err) {
            logger.error(err, "[CheckoutDialogContent] handlePaymentFormSubmit() failed");

            ShowErrorDialog({
                title: "There was a problem upgrading your plan",
                message: err.message
            });
        }

        this.setState({ isSubmitting: false });
    };

    handlePromoChanged = (promo: CheckoutDialogState["promo"]) => {
        this.setState({ promo });
    };

    handleTaxCalculated = (taxAmount: number) => {
        this.setState({ taxAmount });
    };

    handleToggleBillingInterval = () => {
        const { billingInterval } = this.state;
        this.setState({ billingInterval: billingInterval === "month" ? "year" : "month" });
    };

    handleSetBillingInterval = (billingInterval: "month" | "year") => {
        this.setState({ billingInterval });
    };

    render() {
        const { closeDialog } = this.props;
        const { isReady, successfulPurchase, billingInterval, isSubmitting, prices, productDisplayName, promo, availableReferralCredit, taxAmount } = this.state;

        if (!isReady) {
            return (<PreloadContainer>
                <FetchingClickShield visible={true} backgroundColor="white" />
            </PreloadContainer>);
        }

        const currentPrice = prices[billingInterval].amount / 100;

        let currentPromoAdjustedPrice = currentPrice;
        if (promo) {
            if (promo.amountOff) {
                currentPromoAdjustedPrice = Math.max(currentPromoAdjustedPrice - (promo.amountOff / 100), 0);
            } else {
                currentPromoAdjustedPrice = currentPromoAdjustedPrice - (currentPromoAdjustedPrice / 100 * promo.percentOff);
            }
            currentPromoAdjustedPrice = Math.round(currentPromoAdjustedPrice * 100) / 100;
        }

        const isAnnual = billingInterval === "year";

        const annualCostAtMonthlyRate = prices.month.amount * 12 / 100;
        const annualCostAtAnnualRate = prices.year.amount / 100;
        const annualSavingsOnAnnualPlanPercentage = (1 - annualCostAtAnnualRate / annualCostAtMonthlyRate) * 100;

        const showTrial = this.user.availableTrialDays[WorkspacePlanTier.PRO] > 0;

        const dueToday = showTrial
            ? 0
            : Math.max(currentPromoAdjustedPrice - (availableReferralCredit / 100), 0);
        const dueNextBillingCycle = currentPromoAdjustedPrice;

        const taxString = taxAmount ? " plus tax" : "";

        const trialExpirationDate = moment()
            .add(14, "days")
            .format("MMMM Do, YYYY");

        const nextBillingDate = showTrial
            ? trialExpirationDate
            : isAnnual
                ? moment()
                    .add(1, "year")
                    .format("MMMM Do, YYYY")
                : moment()
                    .add(1, "month")
                    .format("MMMM Do, YYYY");

        if (successfulPurchase) {
            return (
                <SuccessfulPurchase
                    productDisplayName={productDisplayName}
                    showTrial={showTrial}
                    trialExpirationDate={trialExpirationDate}
                    callToActionText="Continue"
                    onCallToActionClick={() => {
                        if (closeDialog) {
                            closeDialog(true);
                            return;
                        }

                        const continueUrl = sanitizeRelativeUrl(new URLSearchParams(window.location.search).get("continue") ?? "/");

                        // If the continue URL is the same as the current URL, we need to force a reload in order to update the user's subscription status.
                        if (continueUrl !== window.location.pathname) {
                            window.location.href = continueUrl;
                        } else {
                            window.location.reload();
                        }
                    }
                    }
                />
            );
        }

        if (showTrial) {
            return (
                <>
                    <DialogTitle>
                        Start your Beautiful.ai 14-day trial
                    </DialogTitle>
                    <DialogContent>
                        <FetchingClickShield visible={isSubmitting} backgroundColor="white" />

                        <PriceComparisonContainer>
                            <PriceComparisonRow>
                                <BodySmall>
                                    {isAnnual ? "" : "Monthly"} {productDisplayName} Plan
                                </BodySmall>
                                <AmountPaidContainer>
                                    {isAnnual &&
                                        <StrikedAmount>
                                            ${annualCostAtMonthlyRate}
                                        </StrikedAmount>
                                    }
                                    <BodySmall style={{ fontWeight: 400 }}>
                                        ${currentPromoAdjustedPrice} / {billingInterval}
                                    </BodySmall>
                                </AmountPaidContainer>
                            </PriceComparisonRow>
                            <PriceComparisonRow>
                                <AmountSavedContainer>
                                    {isAnnual &&
                                        <StrikedAmount>
                                            ${annualCostAtMonthlyRate / 12}
                                        </StrikedAmount>
                                    }
                                    <BodySmall style={{ fontWeight: 400 }}>
                                        ${isAnnual
                                            ? `${annualCostAtAnnualRate / 12} x 12 months`
                                            : `${annualCostAtMonthlyRate / 12} x 1 month`}
                                    </BodySmall>
                                </AmountSavedContainer>
                                {isAnnual && <Badge>save {annualSavingsOnAnnualPlanPercentage.toFixed(0)}%</Badge>}
                            </PriceComparisonRow>
                        </PriceComparisonContainer>
                        {/* @ts-ignore */}
                        <Divider margin={15} color="#CCC" />
                        <PriceComparisonContainer>
                            <PriceComparisonRow>
                                <BodySmall>
                                    Due today
                                </BodySmall>
                                <BodySmall>
                                    ${dueToday}{dueToday > 0 ? taxString : ""}
                                </BodySmall>
                            </PriceComparisonRow>
                            <PriceComparisonRow>
                                <BodySmall style={{ fontWeight: 400, color: "#666" }}>
                                    Due {nextBillingDate}
                                </BodySmall>
                                <BodySmall style={{ fontWeight: 400, color: "#666" }}>
                                    ${dueNextBillingCycle}{dueNextBillingCycle > 0 ? taxString : ""}
                                </BodySmall>
                            </PriceComparisonRow>
                        </PriceComparisonContainer>

                        {!isAnnual &&
                            <>
                                <Gap10 />
                                <Row>
                                    <StyledLink
                                        // @ts-ignore
                                        component="button"
                                        variant="body2"
                                        onClick={this.handleToggleBillingInterval}
                                    >
                                        Save {annualSavingsOnAnnualPlanPercentage.toFixed(0)}% with annual billing
                                    </StyledLink>
                                </Row>
                            </>
                        }

                        <Gap30 />

                        <PaymentForm
                            priceId={prices[billingInterval].planId}
                            isFirstTimeTransaction={true}
                            submitLabel={"Start Free Trial"}
                            onPromoChanged={this.handlePromoChanged}
                            onTaxCalculated={this.handleTaxCalculated}
                            disabled={isSubmitting}
                            onSubmit={this.handlePaymentFormSubmit}
                            onCancel={closeDialog ? () => closeDialog(false) : undefined}
                        />

                        <StyledCancelAnytimeText>
                            Your subscription will automatically&nbsp;
                            <StyledCancelAnytimeTextBold>
                                renew {isAnnual ? "annually" : "monthly"}.&nbsp;
                            </StyledCancelAnytimeTextBold>
                            <TermsOfUseAndRefundPolicy />
                        </StyledCancelAnytimeText>

                        {isAnnual &&
                            <>
                                <StyledRow>
                                    <ChangePlanSection>
                                        Pay ${annualCostAtMonthlyRate / 12}/mo (${annualCostAtMonthlyRate}/yr) when you&nbsp;
                                    </ChangePlanSection>
                                    <StyledLink
                                        // @ts-ignore
                                        component="button"
                                        onClick={this.handleToggleBillingInterval}
                                        style={{ textAlign: "left" }}
                                    >switch to monthly billing.</StyledLink>
                                </StyledRow>
                            </>
                        }
                    </DialogContent>
                </>
            );
        }

        return (
            <>
                <DialogTitle>
                    Upgrade to Beautiful.ai <span color="#11a9e2">{productDisplayName}</span>.
                </DialogTitle>
                <DialogContent>
                    <FetchingClickShield visible={isSubmitting} backgroundColor="white" />

                    <LabelLarge>
                        We're sorry but you are not eligible for a 14-day free trial because you have
                        already had a trial in the past.
                    </LabelLarge>
                    <Gap30 />

                    <ChoosePlanHeading>Choose Plan</ChoosePlanHeading>

                    <Select
                        SelectDisplayProps={{ style: { textTransform: "capitalize" } }}
                        style={{ width: "100%" }}
                        variant="outlined"
                        value={billingInterval}
                        inputProps={{ className: "select-input" }}
                        onChange={event => this.handleSetBillingInterval(event.target.value as "month" | "year")}
                    >
                        <MenuItem className="menu-item-with-badge" value="year">
                            Annual Plan - ${annualCostAtAnnualRate / 12}/month
                            <div className="menu-item-badge">Best Value</div>
                        </MenuItem>
                        <MenuItem value="month">Monthly Plan - ${prices.month.amount / 100}/month</MenuItem>
                    </Select>
                    <Gap10 />

                    <LabelLarge>
                        You will be charged <strong>${dueToday.toFixed(2)}{dueToday > 0 ? taxString : ""}</strong> today. Your next bill will be due on{" "}
                        <strong>{nextBillingDate}</strong>.&nbsp;
                        {<TermsOfUseAndRefundPolicy />}
                    </LabelLarge>

                    {availableReferralCredit > 0 && (
                        <div style={{ paddingTop: 10, paddingBottom: 10 }}>
                            <Icon style={{ color: "#fa0", fontSize: "1.25em", verticalAlign: "sub" }}>monetization_on</Icon>{" "}
                            You saved <b>${Math.min(availableReferralCredit / 100, currentPrice)}</b> from referral credit.
                        </div>
                    )}
                    <Gap30 />

                    <PaymentForm
                        priceId={prices[billingInterval].planId}
                        isFirstTimeTransaction={false}
                        submitLabel="Upgrade Now"
                        onPromoChanged={this.handlePromoChanged}
                        onTaxCalculated={this.handleTaxCalculated}
                        disabled={isSubmitting}
                        onSubmit={this.handlePaymentFormSubmit}
                        onCancel={closeDialog ? () => closeDialog(false) : undefined}
                    />
                </DialogContent>
            </>
        );
    }
}

const CheckoutDialog = React.forwardRef(
    function CheckoutDialog(props: CheckoutDialogContentProps & { style?: React.CSSProperties, closeDialog: (upgraded?: boolean) => void }, ref: React.Ref<any>) {
        const { closeDialog, hideBackdrop, style } = props;

        return (
            <BeautifulDialog
                preventClose={true}
                closeDialog={closeDialog}
                hideBackdrop={hideBackdrop}
                style={style}
                PaperProps={{ style: { maxHeight: "100%", minHeight: "500px", backgroundColor: "white", margin: 0 } }}
                ref={ref}
            >
                <CheckoutDialogContent {...props} />
            </BeautifulDialog>
        );
    }
);

export default CheckoutDialog;
